import React, { ReactNode, HTMLAttributes, useMemo } from 'react';
import { NavLink, NavLinkProps, useLocation, matchPath } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import { Badge } from '../Badge';
import { Icon, IconStyle } from '../Icon';
import { universalRenderer } from '../../../utils/universalRenderer';
import './NavTabs.scss';

export type NavTabsNavLink = Omit<NavLinkProps, 'children'> & {
	key: string;
	elementClassName?: string;
	activeClassName?: string;
	label: string;
	children?: ReactNode;
	badge?: ReactNode;
	iconLeft?: string;
	iconLeftStyle?: IconStyle;
	iconRight?: string;
	iconRightStyle?: IconStyle;
	hidden?: boolean;
};

type NavTabsProps = HTMLAttributes<HTMLDivElement> & {
	tabs: NavTabsNavLink[];
	minusMargin?: boolean;
	large?: boolean;
	disabled?: boolean;
	right?: ReactNode | (() => ReactNode);
};

const getPathname = (
	to: string | Partial<{ pathname: string; search: string; hash: string; state: unknown; target: string }>
): string => {
	if (typeof to === 'string') {
		const questionMarkIndex = to.indexOf('?');
		const hashIndex = to.indexOf('#');
		let endIndex = to.length;
		if (questionMarkIndex !== -1 && hashIndex !== -1) {
			endIndex = Math.min(questionMarkIndex, hashIndex);
		} else if (questionMarkIndex !== -1) {
			endIndex = questionMarkIndex;
		} else if (hashIndex !== -1) {
			endIndex = hashIndex;
		}
		return to.substring(0, endIndex);
	}
	return to.pathname || '';
};

export const NavTabs = ({
	tabs,
	className,
	minusMargin = false,
	large = false,
	disabled = false,
	right,
	...props
}: NavTabsProps) => {
	const location = useLocation();

	const tabsWithVisibility = useMemo(() => {
		return tabs.map((tab) => {
			if (!tab.hidden) {
				return { ...tab, isVisible: true };
			}

			const tabPathname = getPathname(tab.to);
			const isMatch = matchPath(tabPathname, location.pathname);
			return { ...tab, isVisible: !!isMatch };
		});
	}, [tabs, location.pathname]);

	const prepareItemLinkLabel = (
		label: string,
		children: ReactNode,
		badge?: ReactNode,
		iconLeft?: string,
		iconRight?: string
	): ReactNode => {
		return (
			<>
				{iconLeft && <Icon name={iconLeft} className="text-rose" />}
				<span className="nav-tabs-link-label" data-text={children || label}>
					{children || label}
				</span>
				{badge ? <Badge>{badge}</Badge> : null}
				{iconRight && <Icon name={iconRight} className="text-rose" />}
			</>
		);
	};

	const visibleTabs = tabsWithVisibility.filter((tab) => tab.isVisible);

	return (
		<nav
			{...props}
			className={classNames(
				'nav-tabs',
				{ 'nav-tabs-minusmargin': minusMargin, 'nav-tabs-large': large },
				className
			)}
		>
			<ul className="nav-tabs-list">
				{visibleTabs.map(
					({
						key,
						label,
						badge,
						elementClassName,
						activeClassName,
						children,
						iconLeft,
						iconLeftStyle,
						iconRight,
						iconRightStyle,
						...rest
					}) => (
						<li key={key} className={classNames('nav-tabs-list-item', elementClassName)}>
							<NavLink
								className={({ isActive }) =>
									classNames(
										'nav-tabs-link',
										{
											'nav-tabs-link-active': isActive,
											'nav-tabs-link-disabled': disabled,
										},
										activeClassName ? { [activeClassName]: isActive } : {}
									)
								}
								aria-label={label}
								aria-disabled={disabled}
								{...rest}
							>
								{prepareItemLinkLabel(label, children, badge, iconLeft, iconRight)}
							</NavLink>
						</li>
					)
				)}
			</ul>
			{right && <div className="flex flex-row gap-2 justify-content-end">{universalRenderer(right)}</div>}
		</nav>
	);
};
